import { BookingChip } from "../components/BookingChip";
import { Icon } from "../components/Icon";
import { PageHeader, plural } from "../components/PageHeader";
import { EmptyState, ErrorState, SkeletonLines } from "../components/States";
import { siteLink } from "../lib/config";
import { bookingsOnDay, newLeads } from "../lib/data";
import { audWhole, fmtLongDay, fmtTime } from "../lib/format";
import { crewLabel, crewNeeded, crewShortfall, leadJob, leadSource } from "../lib/jobs";
import { useAsync } from "../lib/useAsync";
import { MissingTicketsCard, missingTicketsKpi, ticketsInUse, useMissingTickets } from "../modules/field/MissingTickets";
import { SwmsDueCard, swmsSignoffInUse } from "../modules/field/Swms";
import { docketsInUse, UnsignedDocketsCard } from "../modules/field/Docket";
import { billingInUse, BillingTodayCard } from "../modules/billing/Invoices";
import { WhatsNewCard } from "../modules/help/Help";
import { crewMessagesInUse, ShiftRepliesCard } from "../modules/crew-messages/Messages";

export function Today() {
  const bookings = useAsync(() => bookingsOnDay());
  const leads = useAsync(() => newLeads(5));
  const tickets = useMissingTickets();

  const live = bookings.data?.filter((b) => b.status !== "cancelled");
  const rostered = live ? new Set(live.flatMap((b) => b.allocations ?? []).map((a) => a.crew_member_id)).size : undefined;
  const needed = live?.reduce((n, b) => n + crewNeeded(b), 0);
  const short = live?.filter((b) => crewShortfall(b) !== null).length ?? 0;

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow={fmtLongDay(new Date())}
        title="Today"
        actions={
          <>
            <a className="btn btn--outline btn--sm" href={siteLink("/instant-quote/")} target="_blank" rel="noopener">
              New quote <Icon name="external" size={16} /><span className="sr-only"> (opens the public quote form in a new tab)</span>
            </a>
            <a className="btn btn--ink btn--sm" href={siteLink("/book-a-crew/")} target="_blank" rel="noopener">
              New booking <Icon name="external" size={16} /><span className="sr-only"> (opens the public booking form in a new tab)</span>
            </a>
          </>
        }
      />

      <WhatsNewCard />

      <div className="kpis">
        <Kpi label="Shifts today" value={live?.length} loading={bookings.loading} failed={!!bookings.error}
          sub={live ? (short ? `${plural(short, "shift")} short of crew` : live.length ? "All confirmed shifts crewed" : "Nothing booked") : undefined} />
        <Kpi label="Controllers rostered" value={rostered} loading={bookings.loading} failed={!!bookings.error}
          sub={needed !== undefined ? (needed ? `of ${needed} needed today` : "No crew needed today") : undefined} />
        <Kpi label="New leads" value={leads.data?.count} loading={leads.loading} failed={!!leads.error}
          sub={leads.data ? "Waiting for first contact" : undefined} href="#/leads" />
        {ticketsInUse() && <Kpi {...missingTicketsKpi(tickets)} alert />}
      </div>

      <div className="today-grid">
        <div className="card card--table today-grid__bookings">
          <div className="card__head">
            <h2 className="card__title">Bookings today</h2>
            <a className="card__link" href="#/bookings">Open bookings <Icon name="arrow" size={16} /></a>
          </div>
          {bookings.loading && <SkeletonLines rows={5} />}
          {bookings.error && <ErrorState message={bookings.error} onRetry={bookings.reload} />}
          {bookings.data && bookings.data.length === 0 && (
            <EmptyState icon="calendar" title="No bookings today">New booking requests from the website appear here.</EmptyState>
          )}
          {bookings.data && bookings.data.length > 0 && (
            <table className="table table--stack table--compact">
              <thead>
                <tr><th scope="col">Start</th><th scope="col">Site / customer</th><th scope="col">Crew</th><th scope="col">Status</th></tr>
              </thead>
              <tbody>
                {bookings.data.map((b) => (
                  <tr key={b.id}>
                    <td data-label="Start" className="strong nowrap">{fmtTime(b.starts_at)}</td>
                    <td data-label="Site">
                      {b.site_address ?? "Site not set"}
                      <span className="sub">{b.company ?? b.contact_name ?? "No customer name"}</span>
                    </td>
                    <td data-label="Crew" className="nowrap">
                      {crewLabel(b)}
                      {b.allocations && b.status !== "requested" && <span className="sub">{b.allocations.length} of {crewNeeded(b)} allocated</span>}
                    </td>
                    <td data-label="Status"><BookingChip b={b} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="today-grid__side">
          <div className="card">
            <div className="card__head">
              <h2 className="card__title">New leads</h2>
              <a className="card__link" href="#/leads">All leads <Icon name="arrow" size={16} /></a>
            </div>
            {leads.loading && <SkeletonLines rows={4} />}
            {leads.error && <ErrorState message={leads.error} onRetry={leads.reload} />}
            {leads.data && leads.data.items.length === 0 && <EmptyState icon="inbox" title="Inbox clear">No leads waiting for contact.</EmptyState>}
            {leads.data && leads.data.items.length > 0 && (
              <ul className="list">
                {leads.data.items.map((l) => (
                  <li key={l.id} className="list__row">
                    <span>
                      <strong>{l.name}</strong>, {leadSource(l.source).toLowerCase()}
                      <span className="sub">{leadJob(l)}</span>
                    </span>
                    {l.estimate_result?.total !== undefined
                      ? <span className="list__value">{audWhole(l.estimate_result.total)}</span>
                      : <span className="list__value list__value--muted">No estimate</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {crewMessagesInUse() && <ShiftRepliesCard />}
          {docketsInUse() && <UnsignedDocketsCard />}
          {billingInUse() && <BillingTodayCard />}
          {swmsSignoffInUse() && <SwmsDueCard />}
          {ticketsInUse() && <MissingTicketsCard state={tickets} />}
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, sub, loading, failed, alert, href }: {
  label: string; value: number | undefined; sub?: string; loading: boolean; failed: boolean; alert?: boolean; href?: string;
}) {
  const body = (
    <>
      <span className="kpi__label">{label}</span>
      <span className="kpi__value">{loading ? <span className="skeleton skeleton--kpi" /> : failed ? <span className="kpi__na">Unavailable</span> : value}</span>
      {sub && !loading && <span className="kpi__sub">{sub}</span>}
    </>
  );
  const cls = `kpi${alert ? " kpi--alert" : ""}${alert && value ? " kpi--hot" : ""}`;
  return href ? <a className={`${cls} kpi--link`} href={href}>{body}</a> : <div className={cls}>{body}</div>;
}
